import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import {useLiveQuery} from 'dexie-react-hooks';
import { useServices, useSession, useConnection, useServerConfig,useLive, ProblemError, type HyloServices, type RequestOptions, type HttpMethod } from '@/model';
import { journalFor, removeAccountJournals,type Intent } from './journal';
import { decodeItem } from './domain';
import type { ItemContext, ItemRecord, Receipt } from './types';
import { spacesServices } from './transport';
interface Runtime {
    generation: number;
    lifetime: number;
    subscriptions: number;
    fileVersions: Map<string, number>;
    stop?: () => void;
}
const runtimes = new WeakMap<HyloServices, Runtime>();
export function useSpacesFlags(){return useLive(spacesServices(useServices()).flags.flags());}
function runtimeFor(services: HyloServices): Runtime {
    let runtime = runtimes.get(services);
    if (!runtime) {
        runtime = { generation: 0, lifetime: 0, subscriptions: 0, fileVersions: new Map() };
        runtimes.set(services, runtime);
        const captured = runtime;
        // The cleanup registration survives leaving Spaces, so later explicit removal
        // still clears this feature's retained work for the captured account only.
        services.session.onRetire(async (event) => {
            captured.generation++;
            captured.lifetime++;
            captured.fileVersions.clear();
            if (event.reason === 'remove' || event.reason === 'deleted')
                await removeAccountJournals(window.location.origin, event.accountId);
        });
    }
    return runtime;
}
/** Services-phase startup and visited routes share exactly one retirement installer. */
export function installSpacesCleanup(services:HyloServices):void { runtimeFor(spacesServices(services)); }
function sameAccount(services: HyloServices, account: string) { const session = services.session.state.get(); return session.phase === 'signedIn' && session.accountId === account; }
export function isLost(error: unknown): boolean { return error instanceof ProblemError && [401, 403, 404].includes(error.status); }
export function isConflict(error: unknown): boolean { return error instanceof ProblemError && [409, 412, 428].includes(error.status) || error instanceof Error && ['item_revision_mismatch', 'file_changed', 'plan_changed'].includes(error.message); }
export function useSpacesScope() {
    const services = spacesServices(useServices());
    const session = useSession();
    const account = session.phase === 'signedIn' ? session.accountId : '';
    const origin = window.location.origin;
    const client = useQueryClient();
    const runtime = runtimeFor(services);
    const config = useServerConfig();
    const discovery = useQuery({ queryKey: ['hylo', origin, 'context', account, 'items-context'], enabled: !!account,
        queryFn: ({ signal }) => services.http.get<ItemContext>('/v1/items/context', { signal }), retry: false, staleTime: 30000 });
    const rawHint = config?.item_context;
    const hint = rawHint && typeof rawHint === 'object' && typeof (rawHint as ItemContext).realm_id === 'string' && typeof (rawHint as ItemContext).mutation_epoch === 'string' && Array.isArray((rawHint as ItemContext).capabilities) && (rawHint as ItemContext).capabilities.every(value => typeof value === 'string') ? rawHint as ItemContext : undefined;
    const context = { ...discovery, data: discovery.data ?? hint };
    const realm = context.data?.realm_id ?? origin;
    const prefix = useMemo(() => ['hylo', origin, realm, account] as const, [origin, realm, account]);
    const knownRealm = context.data?.realm_id;
    const db = useMemo(() => account && knownRealm ? journalFor(origin, knownRealm, account) : undefined, [origin, knownRealm, account]);
    useEffect(() => {
        if (!account)
            return;
        runtime.subscriptions++;
        if (!runtime.stop) {
            const retire = () => {
                runtime.generation++;
                runtime.lifetime++;
                runtime.fileVersions.clear();
                void client.cancelQueries({ queryKey: ['hylo', origin] });
                // Removing an active Query alone does not notify its observers: reset first,
                // so authorized projections disappear before a fresh request can settle.
                void client.resetQueries({ queryKey: ['hylo', origin], predicate: q => q.queryKey.includes('spaces') || q.queryKey.includes('item') || q.queryKey.includes('items-context') });
                client.removeQueries({ queryKey: ['hylo', origin], type: 'inactive' });
            };
            let phase = services.sync.connection.get().phase;
            let folderFreshAfter = services.sync.connection.get().since;
            let login = services.session.lifetime.get();
            const stopLifetime = services.session.lifetime.subscribe(() => {
                const next = services.session.lifetime.get();
                if (next?.loginId !== login?.loginId || next?.generation !== login?.generation) {
                    runtime.fileVersions.clear();
                    retire();
                }
                login = next;
            });
            const stopSession = services.session.state.subscribe(() => {
                if (!sameAccount(services, account))
                    retire();
            });
            const stopConnection = services.sync.connection.subscribe(() => {
                const connection = services.sync.connection.get(), next = connection.phase;
                if (next === 'offline' || next === 'resyncing') folderFreshAfter = connection.since;
                if (next === 'resyncing' && phase !== next)
                    retire();
                if (next === 'online' && phase !== next)
                    // The badge and Files already share metadata read during this connection attempt.
                    void client.invalidateQueries({ queryKey: ['hylo', origin], predicate: query => {
                        const key = query.queryKey, folder = key.at(-5) === 'spaces' && key.at(-4) === 'project' && key.at(-2) === 'folder-metadata';
                        return !folder || query.state.dataUpdatedAt < folderFreshAfter;
                    } });
                phase = next;
            });
            const stopUpdates = services.sync.onUpdate('*', update => {
                const type = update.type;
                if (!/^(item\.|project\.|calendar\.|label\.|account\.labels|tag[._]|dashboard\.|conversation\.|message\.|block\.|contact\.|org\.)/.test(type))
                    return;
                const loss = type === 'item.removed' || type === 'project.removed' && update.data.project_kept !== true || type === 'block.changed' || type === 'contact.removed';
                if (type === 'message.deleted' || type === 'message.hidden' || type === 'project.removed' && update.data.project_kept === true) {
                    runtime.generation++;
                    const erased = update.data.erased_conversation_id;
                    const sourceQueries = (q: {
                        queryKey: QueryKey;
                    }) => q.queryKey.includes('message-source') || q.queryKey.includes('spaces')&&q.queryKey.includes('project') || q.queryKey.includes('journal')&&(typeof erased!=='string'||q.queryKey.includes(erased));
                    void client.cancelQueries({ queryKey: ['hylo', origin], predicate: sourceQueries });
                    void client.resetQueries({ queryKey: ['hylo', origin], predicate: sourceQueries });
                }
                if (loss) {
                    runtime.generation++;
                    void client.cancelQueries({ queryKey: ['hylo', origin] });
                    void client.resetQueries({ queryKey: ['hylo', origin], predicate: q => q.queryKey.includes('spaces') || q.queryKey.includes('item') });
                    client.removeQueries({ queryKey: ['hylo', origin], type: 'inactive', predicate: q => q.queryKey.includes('spaces') || q.queryKey.includes('item') });
                }
                else if (/^(item\.(changed|mine\.changed)|project\.member\.|calendar\.sharing\.|account\.labels\.)/.test(type)) {
                    runtime.generation++;
                    void client.cancelQueries({ queryKey: ['hylo', origin], predicate: q => q.queryKey.includes('spaces') || q.queryKey.includes('item') });
                    void client.resetQueries({queryKey:['hylo',origin],predicate:q=>q.queryKey.includes('spaces')||q.queryKey.includes('item')});
                }
                void client.invalidateQueries({ queryKey: ['hylo', origin], predicate: q => {
                        if (type.startsWith('message.'))
                            return q.queryKey.includes('message-source') || q.queryKey.includes('project') && (q.queryKey.includes('search') || q.queryKey.includes('dashboard'));
                        if (type.startsWith('conversation.'))
                            return q.queryKey.includes('spaces') || q.queryKey.includes('item');
                        return q.queryKey.includes('spaces') || q.queryKey.includes('item') || q.queryKey.includes('item-preferences') || q.queryKey.includes('items-context');
                    } });
            });
            const stopSignals = services.sync.onSignal(['project.files.changed'], signal => {
                if (!sameAccount(services, account) || signal.type !== 'project.files.changed' || typeof signal.project_id !== 'string' || typeof signal.version !== 'number' || !Number.isSafeInteger(signal.version) || signal.version < 0)
                    return;
                const previous = runtime.fileVersions.get(signal.project_id);
                if (previous !== undefined && signal.version <= previous)
                    return;
                runtime.fileVersions.set(signal.project_id, signal.version);
                runtime.generation++;
                const projectId = signal.project_id;
                const related = (q: {
                    queryKey: QueryKey;
                }) => q.queryKey.includes('spaces') && q.queryKey.includes('project') && q.queryKey.includes(projectId) && !q.queryKey.includes('document-source');
                void client.cancelQueries({ queryKey: ['hylo', origin], predicate: related });
                void client.invalidateQueries({ queryKey: ['hylo', origin], predicate: related });
            });
            runtime.stop = () => { stopSession(); stopLifetime(); stopConnection(); stopUpdates(); stopSignals(); runtime.stop = undefined; };
        }
        return () => {
            if (--runtime.subscriptions === 0)
                runtime.stop?.();
        };
    }, [account, services, client, runtime, origin]);
    return { services, account, origin, realm, context, prefix, db, runtime, client };
}
export function useResource<T>(tail: QueryKey, path: string, options?: {
    query?: RequestOptions['query'];
    enabled?: boolean;
    select?: (value: unknown) => T;
    staleTime?: number;
}) {
    const scope = useSpacesScope();
    const query = useQuery<T>({
        queryKey: [...scope.prefix, ...tail, options?.query ?? {}],
        enabled: !!scope.account && options?.enabled !== false,
        queryFn: async ({ signal }) => {
            const generation = scope.runtime.generation;
            const value = await scope.services.http.get<unknown>(path, { query: options?.query, signal });
            if (generation !== scope.runtime.generation || !sameAccount(scope.services, scope.account))
                throw new Error('session_changed');
            return options?.select ? options.select(value) : value as T;
        },
        retry: (count, error) => !isLost(error) && !isConflict(error) && count < 1,
        staleTime: options?.staleTime ?? 15000,
        refetchOnWindowFocus: true,
    });
    // Consumers use resource names in toolbars as well as inside ResourceState.
    // An authorization error must also hide those cached header projections.
    return {...query,data:isLost(query.error)?undefined:query.data};
}
/** Share the decoded preference shape used by Calendar and Me; never persist a device fallback. */
export function useItemDisplayZone() {
    const scope=useSpacesScope();
    const preferences=useQuery({queryKey:[...scope.prefix,'item-preferences'],enabled:!!scope.account&&!!scope.context.data,staleTime:30000,retry:false,
        queryFn:async({signal})=>{
            const generation=scope.runtime.generation;
            const value=await scope.services.http.get<{preferences:{time_zone:string|null}}>('/v1/me/item-preferences',{signal});
            if(signal.aborted||generation!==scope.runtime.generation||!sameAccount(scope.services,scope.account))throw new Error('session_changed');
            return value.preferences;
        }});
    let zone=preferences.data?.time_zone;
    try{if(typeof zone!=='string'||!zone)zone=undefined;else new Intl.DateTimeFormat('en',{timeZone:zone});}catch{zone=undefined;}
    return {timeZone:zone??Intl.DateTimeFormat().resolvedOptions().timeZone,ready:!preferences.isPending};
}
export function useItem(id: string) {
    const query = useResource<{
        item: ItemRecord;
    }>(['item', id], `/v1/items/${encodeURIComponent(id)}`, { enabled: !!id, select: value => ({ item: decodeItem((value as {
                item: unknown;
            }).item) }) });
    return { ...query, data: isLost(query.error) ? undefined : query.data };
}
export function useWrite(recovery?:{key?:string;onRecovered?:(target?:Intent['resultTarget'])=>void|Promise<void>}) {
    const scope = useSpacesScope();
    const [pending, setPending] = useState(false);
    const busy = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const [error, setError] = useState<unknown>();
    const [uncertainId, setUncertain] = useState<string>();
    const recoveryRef=useRef(recovery);recoveryRef.current=recovery;
    const operationKey=recovery?.key;
    const retained=useLiveQuery(async()=>scope.db&&operationKey?(await scope.db.intents.where('key').equals(operationKey).sortBy('createdAt')).at(-1):undefined,[scope.db,operationKey]);
    const uncertain=uncertainId??(retained&&['sending','unknown'].includes(retained.state)?retained.id:undefined);
    async function write<T>(key: string, method: HttpMethod, path: string, body: Record<string, unknown>, options?: {
        native?: boolean;
        keyed?: boolean;
        headers?: Record<string, string>;
        draftKey?: string;
        draftAt?: number;
        epoch?: string;
        draftValue?: unknown;
    }): Promise<T> {
        if (busy.current)
            throw new Error('write_pending');
        busy.current = true;
        setPending(true);
        setError(undefined);
        const generation = scope.runtime.lifetime;
        const account = scope.account;
        let intentId: string | undefined;
        let dispatched = false;
        try {
            if (scope.services.sync.connection.get().phase !== 'online')
                throw new Error('offline');
            const context = await scope.services.http.get<ItemContext>('/v1/items/context');
            if (!scope.db || !account || generation !== scope.runtime.lifetime || !sameAccount(scope.services, account))
                throw new Error('session_changed');
            const epoch = context.mutation_epoch;
            if (options?.epoch && options.epoch !== epoch)
                throw new Error('epoch_changed');
            const draftEpoch=options?.draftKey?(await scope.db.drafts.get(options.draftKey))?.epoch:undefined;
            if(draftEpoch&&draftEpoch!==epoch)throw new Error('epoch_changed');
            const intent = await scope.db.prepare(key, epoch, method, path, body, options?.headers,options?.keyed!==false||!!options?.draftKey,options?.draftKey&&options.draftAt!==undefined&&options.draftValue!==undefined?{key:options.draftKey,updatedAt:options.draftAt,value:options.draftValue}:undefined);
            intentId = intent.id;
            if(options?.keyed===false&&['unknown','sending'].includes(intent.state))throw new Error('outcome_unknown');
            if (intent.state === 'committed') {
                if (intent.resultTarget?.kind === 'project')
                    return await scope.services.http.get<T>(`/v1/projects/${intent.resultTarget.id}`);
                if (intent.resultTarget?.kind === 'item') {
                    const current = await scope.services.http.get<{
                        item: unknown;
                    }>(`/v1/items/${intent.resultTarget.id}`);
                    return { status: 'committed', item: current.item, undo_id: null, receipt: null } as T;
                }
                return undefined as T;
            }
            await scope.db.intents.update(intent.id, { state: 'sending' });
            // Proofs remain transient; only the immutable reviewed intent is retained.
            const payload = options?.keyed === false ? body : { ...body, client_id: intent.id };
            dispatched = true;
            const result = await scope.services.http.request<T>(method, path, { body: payload, headers: { 'X-Hylo-Mutation-Epoch': epoch, ...options?.headers } });
            if (generation !== scope.runtime.lifetime || !sameAccount(scope.services, account))
                throw new Error('session_changed');
            const data = result && typeof result === 'object' ? result as Record<string, unknown> : {};
            const target = data.item && typeof data.item === 'object' ? data.item as Record<string, unknown> : data.project && typeof data.project === 'object' ? data.project as Record<string, unknown> : undefined;
            const resultTarget = target && typeof target.id === 'string' ? { kind: data.item ? 'item' as const : 'project' as const, id: target.id } : undefined;
            await scope.db.acknowledge(intent.id, options?.draftKey, options?.draftAt, resultTarget, options?.draftValue);
            scope.runtime.generation++;
            await scope.client.cancelQueries({ queryKey: scope.prefix });
            void scope.client.invalidateQueries({ queryKey: scope.prefix });
            setUncertain(undefined);
            if (!mounted.current)
                throw new Error('presentation_closed');
            return result;
        }
        catch (failure) {
            const unknown = dispatched && !(failure instanceof ProblemError)||failure instanceof Error&&failure.message==='outcome_unknown';
            if (intentId && scope.db && generation === scope.runtime.lifetime && (await scope.db.intents.get(intentId))?.state !== 'committed') {
                await scope.db.intents.update(intentId, { state: unknown ? 'unknown' : isConflict(failure) ? 'review' : 'prepared' });
                if (unknown)
                    setUncertain(intentId);
            }
            setError(failure);
            throw failure;
        }
        finally {
            busy.current = false;
            setPending(false);
        }
    }
    async function checkStatus() {
        if (!uncertain || !scope.db||busy.current)
            return false;
        busy.current=true;
        setPending(true);
        const generation=scope.runtime.lifetime;
        try {
            const intent = await scope.db.intents.get(uncertain);
            if (!intent)
                return false;
            const result = await scope.services.http.get<{
                mutation_epoch: string;
                results: Receipt[];
            }>(`/v1/items/requests/${intent.id}`, { query: { mutation_epoch: intent.epoch } });
            if (result.mutation_epoch !== intent.epoch || !result.results.length || result.results.some(receipt => receipt.status !== 'committed'))
                throw new Error('receipt_unavailable');
            if(generation!==scope.runtime.lifetime||!sameAccount(scope.services,scope.account))throw new Error('session_changed');
            const item = result.results.find(receipt => receipt.item_id && receipt.op_id === null);
            const kind=item?.operation==='native_project_create'||intent.method==='POST'&&intent.path==='/v1/projects'?'project':'item';
            const target=item?.item_id ? {kind:kind as 'item'|'project',id:item.item_id}:undefined;
            await scope.db.acknowledge(intent.id, undefined, undefined,target);
            if(generation!==scope.runtime.lifetime||!sameAccount(scope.services,scope.account))throw new Error('session_changed');
            setUncertain(undefined);
            setError(undefined);
            void scope.client.invalidateQueries({ queryKey: scope.prefix });
            if(mounted.current&&(!recoveryRef.current?.key||recoveryRef.current.key===intent.key))await recoveryRef.current?.onRecovered?.(target);
            return true;
        }
        catch {
            setError(new Error('receipt_unavailable'));
            return false;
        }
        finally{busy.current=false;if(mounted.current)setPending(false);}
    }
    return { write, pending, error, uncertain, checkStatus, clearError: () => setError(undefined) };
}
export function useForegroundRefresh(active: boolean, refresh: () => unknown) {
    const connection = useConnection();
    const stableRefresh = useCallback(refresh, [refresh]);
    useEffect(() => {
        if (!active || connection.phase !== 'online')
            return;
        let running = false;
        const run = () => {
            if (document.visibilityState !== 'visible' || running)
                return;
            running = true;
            Promise.resolve(stableRefresh()).finally(() => { running = false; }).catch(() => undefined);
        };
        // Files metadata now arrives through the core's ordered ephemeral signals.
        // Focus/manual refresh remains useful after a missed or disconnected signal.
        window.addEventListener('focus', run);
        document.addEventListener('visibilitychange', run);
        return () => { window.removeEventListener('focus', run); document.removeEventListener('visibilitychange', run); };
    }, [active, connection.phase, stableRefresh]);
}
export async function invalidateSpaces(client: QueryClient, prefix: QueryKey) { await client.invalidateQueries({ queryKey: prefix }); }
